// Жесты просмотрщика фото: щипок двумя пальцами, двойной тап/двойной клик, pan пальцем/мышью/колесом
// при увеличении, Ctrl+колесо (щипок тачпада) и — прежнее поведение — свайп/колесо листают элементы
// в исходном виде. Математика — util/zoom.ts; здесь только события и состояние.
//
// Почему нативные слушатели, а не React-пропсы: React вешает touchstart/touchmove/wheel ПАССИВНЫМИ —
// preventDefault там не работает, и браузер сам зумил/скроллил страницу (Ctrl+колесо на desktop,
// щипок в iOS Safari, который игнорирует user-scalable=no). Поэтому touch*/wheel/gesture* — через
// addEventListener({ passive: false }) на сцене; мышь (mousedown/dblclick) — обычные React-пропсы.
//
// Состояние зума привязано к resetKey (id элемента + источник оригинала): другой элемент или пришедший
// оригинал вместо превью — снова исходный вид, без отдельного «сброса» по эффекту (нет кадра со старым зумом).
import { useCallback, useEffect, useLayoutEffect, useRef, useState, } from 'react';
import { ZOOM_IDENTITY, clampPan, distance, isZoomed, panBy, pinchTransform, toBoxPoint, toggleZoomAt, wheelDeltaPx, wheelZoomFactor, zoomAt, } from '../util/zoom.js';
/** Двойной тап: второй тап начат не позже чем через 300 мс после первого и в пределах 25 px. */
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_SLOP = 25;
/** Тап — короткое касание без сдвига. */
const TAP_MAX_MS = 300;
const TAP_SLOP = 10;
/** Сдвиг, после которого жест считается перемещением (подавляет клик-хвост). */
const MOVE_SLOP = 3;
/** Совместимые mouse-события после касания (mousedown/dblclick) — игнорируем это окно. */
const TOUCH_MOUSE_GUARD_MS = 800;
/** Клик-хвост после жеста/двойного тапа не закрывает просмотрщик. */
const CLICK_SUPPRESS_MS = 450;
/** Рамка из DOM: сцена (getBoundingClientRect) + раскладочный бокс картинки (offset*, трансформ не влияет). */
export function measureZoomFrame(stage) {
    const img = stage.querySelector('[data-viewer-image]');
    if (img === null)
        return null;
    const r = stage.getBoundingClientRect();
    const boxW = img.offsetWidth;
    const boxH = img.offsetHeight;
    // картинка ещё без размеров (не декодирована) — масштабировать не во что
    if (!(boxW > 0 && boxH > 0 && r.width > 0 && r.height > 0))
        return null;
    return {
        stageLeft: r.left,
        stageTop: r.top,
        stageW: r.width,
        stageH: r.height,
        boxLeft: img.offsetLeft,
        boxTop: img.offsetTop,
        boxW,
        boxH,
    };
}
/** Касания из TouchList (тестовые события могут прийти без списка или без clientY/identifier). */
function touchesOf(list) {
    if (list === undefined || list === null)
        return [];
    const out = [];
    for (let i = 0; i < list.length; i++) {
        const t = list[i];
        if (t === undefined || t === null)
            continue;
        out.push({
            id: typeof t.identifier === 'number' ? t.identifier : i,
            p: { x: Number(t.clientX) || 0, y: Number(t.clientY) || 0 },
        });
    }
    return out;
}
function isOnButton(target) {
    return target instanceof Element && target.closest('button') !== null;
}
export function useZoomPan(opts) {
    const { stageRef, active, zoomable, resetKey } = opts;
    const [state, setState] = useState(() => ({
        key: resetKey,
        t: ZOOM_IDENTITY,
        animate: false,
    }));
    const [gesturing, setGesturing] = useState(false);
    // Эффективное состояние: чужой ключ (другой элемент/пришёл оригинал) или не-масштабируемый элемент — исходный вид.
    const effective = state.key === resetKey && zoomable ? state : null;
    const transform = effective?.t ?? ZOOM_IDENTITY;
    const animating = effective?.animate ?? false;
    // Свежие значения для нативных слушателей (вешаются один раз на маунт сцены).
    const live = useRef({
        key: resetKey,
        zoomable,
        onSwipe: opts.onSwipe,
        onWheelNavigate: opts.onWheelNavigate,
        swipeThreshold: opts.swipeThreshold,
        measure: opts.measure ?? measureZoomFrame,
    });
    live.current = {
        key: resetKey,
        zoomable,
        onSwipe: opts.onSwipe,
        onWheelNavigate: opts.onWheelNavigate,
        swipeThreshold: opts.swipeThreshold,
        measure: opts.measure ?? measureZoomFrame,
    };
    // Преобразование «как сейчас» для обработчиков: пишется сразу при коммите (несколько событий колеса
    // до ре-рендера не теряют шаги) и синхронизируется с отрисованным (сброс по ключу).
    const tRef = useRef(transform);
    useLayoutEffect(() => {
        tRef.current = transform;
    }, [transform]);
    const gestureRef = useRef(null);
    const dragRef = useRef(null);
    const dragCleanupRef = useRef(null);
    const lastTapRef = useRef(null);
    const lastTouchEndRef = useRef(0);
    const suppressClickUntilRef = useRef(0);
    const gesturingRef = useRef(false);
    const commit = useCallback((next, animate) => {
        tRef.current = next;
        setState({ key: live.current.key, t: next, animate });
    }, []);
    const setGesture = useCallback((on) => {
        if (gesturingRef.current === on)
            return;
        gesturingRef.current = on;
        setGesturing(on);
    }, []);
    // Другой элемент — забыть его зум (иначе при возврате к прежнему элементу ключ совпал бы и старый
    // зум «воскрес»), незавершённый жест и «первый тап». Рендер уже показал 1× (effective), здесь — до paint.
    useLayoutEffect(() => {
        setState((s) => (s.key === resetKey ? s : { key: resetKey, t: ZOOM_IDENTITY, animate: false }));
        gestureRef.current = null;
        lastTapRef.current = null;
        dragCleanupRef.current?.();
        setGesture(false);
    }, [resetKey, setGesture]);
    useEffect(() => {
        if (!active)
            return;
        const stage = stageRef.current;
        if (stage === null)
            return;
        const root = stage.closest('[data-media-viewer]') ?? stage;
        const now = () => Date.now();
        /** Перебазировать жест на текущие пальцы (смена их числа: щипок↔pan без скачка картинки). */
        const rebase = (g, touches) => {
            const pts = touches.slice(0, 2);
            g.ids = pts.map((t) => t.id);
            g.startPts = pts.map((t) => t.p);
            g.startT = tRef.current;
            g.frame = live.current.zoomable ? live.current.measure(stage) : null;
        };
        const onTouchStart = (e) => {
            const touches = touchesOf(e.touches);
            if (touches.length === 0)
                return;
            let g = gestureRef.current;
            if (g === null) {
                const first = touches[0];
                g = {
                    origin: first.p,
                    t0: now(),
                    last: first.p,
                    ids: [],
                    startPts: [],
                    startT: tRef.current,
                    frame: null,
                    multi: touches.length > 1,
                    moved: false,
                    startedZoomed: isZoomed(tRef.current),
                    onButton: isOnButton(e.target),
                };
                gestureRef.current = g;
            }
            else if (touches.length > 1) {
                g.multi = true;
            }
            rebase(g, touches);
            // Два пальца на фото — наш щипок, а не системный зум страницы.
            if (touches.length > 1 && live.current.zoomable)
                e.preventDefault();
        };
        const onTouchMove = (e) => {
            const g = gestureRef.current;
            if (g === null)
                return;
            const touches = touchesOf(e.touches);
            const byId = (id) => touches.find((t) => t.id === id)?.p;
            const first = byId(g.ids[0] ?? -1) ?? touches[0]?.p;
            if (first !== undefined)
                g.last = first;
            if (!live.current.zoomable || g.frame === null)
                return;
            if (g.ids.length >= 2) {
                const a = byId(g.ids[0]);
                const b = byId(g.ids[1]);
                const a0 = g.startPts[0];
                const b0 = g.startPts[1];
                if (a === undefined || b === undefined || a0 === undefined || b0 === undefined)
                    return;
                e.preventDefault();
                const next = pinchTransform(g.startT, toBoxPoint(a0, g.frame), toBoxPoint(b0, g.frame), toBoxPoint(a, g.frame), toBoxPoint(b, g.frame), g.frame);
                g.moved = true;
                setGesture(true);
                commit(next, false);
                return;
            }
            // Один палец: pan — только по увеличенному фото; в исходном виде это свайп (решается на touchend).
            if (!isZoomed(g.startT) || first === undefined)
                return;
            const p0 = g.startPts[0];
            if (p0 === undefined)
                return;
            e.preventDefault();
            const dx = first.x - p0.x;
            const dy = first.y - p0.y;
            if (Math.abs(dx) > MOVE_SLOP || Math.abs(dy) > MOVE_SLOP)
                g.moved = true;
            setGesture(true);
            commit(panBy(g.startT, dx, dy, g.frame), false);
        };
        const finish = (g, end, cancelled) => {
            const t = now();
            gestureRef.current = null;
            lastTouchEndRef.current = t;
            setGesture(false);
            if (g.moved || g.multi)
                suppressClickUntilRef.current = t + CLICK_SUPPRESS_MS;
            if (cancelled)
                return;
            // Свайп листает только исходный вид и только одним пальцем (прежний порог).
            const dx = end.x - g.origin.x;
            if (!g.multi && !g.startedZoomed && !isZoomed(tRef.current) && Math.abs(dx) >= live.current.swipeThreshold) {
                live.current.onSwipe(dx > 0 ? 'prev' : 'next');
                lastTapRef.current = null;
                return;
            }
            const isTap = !g.multi && !g.onButton && distance(end, g.origin) < TAP_SLOP && t - g.t0 < TAP_MAX_MS;
            if (!isTap) {
                lastTapRef.current = null;
                return;
            }
            const prev = lastTapRef.current;
            if (prev !== null &&
                live.current.zoomable &&
                g.t0 - prev.t <= DOUBLE_TAP_MS &&
                distance(prev.p, end) <= DOUBLE_TAP_SLOP) {
                lastTapRef.current = null;
                const frame = live.current.measure(stage);
                if (frame !== null) {
                    commit(toggleZoomAt(tRef.current, toBoxPoint(end, frame), frame), true);
                    // клик-хвост второго тапа не должен закрыть просмотрщик (фото только что вернулось к 1×)
                    suppressClickUntilRef.current = t + CLICK_SUPPRESS_MS;
                }
                return;
            }
            lastTapRef.current = { t, p: end };
        };
        const onTouchEnd = (e) => {
            const g = gestureRef.current;
            if (g === null)
                return;
            const remaining = touchesOf(e.touches);
            if (remaining.length > 0) {
                // отпустили один из пальцев — продолжаем оставшимися (щипок → pan) с новой опорой
                rebase(g, remaining);
                return;
            }
            const end = touchesOf(e.changedTouches)[0]?.p ?? g.last;
            finish(g, end, false);
        };
        const onTouchCancel = () => {
            const g = gestureRef.current;
            if (g === null)
                return;
            finish(g, g.last, true);
        };
        const onWheel = (e) => {
            const z = tRef.current;
            if (live.current.zoomable && e.ctrlKey) {
                // Ctrl+колесо / щипок тачпада — зум в точке курсора (и не зум всей страницы браузером)
                e.preventDefault();
                const frame = live.current.measure(stage);
                if (frame === null)
                    return;
                const anchor = toBoxPoint({ x: e.clientX, y: e.clientY }, frame);
                commit(zoomAt(z, z.scale * wheelZoomFactor(e.deltaY, e.deltaMode), anchor, frame), false);
                return;
            }
            if (live.current.zoomable && isZoomed(z)) {
                // увеличено: колесо/двухпальцевый скролл тачпада двигают фото, а не листают
                e.preventDefault();
                const frame = live.current.measure(stage);
                if (frame === null)
                    return;
                commit(panBy(z, -wheelDeltaPx(e.deltaX, e.deltaMode), -wheelDeltaPx(e.deltaY, e.deltaMode), frame), false);
                return;
            }
            // прежнее поведение: горизонтальная прокрутка/шифт-колесо листает (desktop)
            const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
            if (delta > live.current.swipeThreshold)
                live.current.onWheelNavigate('next');
            else if (delta < -live.current.swipeThreshold)
                live.current.onWheelNavigate('prev');
        };
        // iOS Safari: собственный жест масштабирования страницы (игнорирует user-scalable=no) — гасим.
        const blockGesture = (e) => e.preventDefault();
        // Поворот/ресайз: тот же масштаб, сдвиг — в новых границах.
        const onResize = () => {
            const z = tRef.current;
            if (!isZoomed(z))
                return;
            const frame = live.current.measure(stage);
            if (frame !== null)
                commit(clampPan(z, frame), false);
        };
        stage.addEventListener('touchstart', onTouchStart, { passive: false });
        stage.addEventListener('touchmove', onTouchMove, { passive: false });
        stage.addEventListener('touchend', onTouchEnd);
        stage.addEventListener('touchcancel', onTouchCancel);
        stage.addEventListener('wheel', onWheel, { passive: false });
        root.addEventListener('gesturestart', blockGesture, { passive: false });
        root.addEventListener('gesturechange', blockGesture, { passive: false });
        root.addEventListener('gestureend', blockGesture, { passive: false });
        window.addEventListener('resize', onResize);
        return () => {
            stage.removeEventListener('touchstart', onTouchStart);
            stage.removeEventListener('touchmove', onTouchMove);
            stage.removeEventListener('touchend', onTouchEnd);
            stage.removeEventListener('touchcancel', onTouchCancel);
            stage.removeEventListener('wheel', onWheel);
            root.removeEventListener('gesturestart', blockGesture);
            root.removeEventListener('gesturechange', blockGesture);
            root.removeEventListener('gestureend', blockGesture);
            window.removeEventListener('resize', onResize);
            gestureRef.current = null;
            dragCleanupRef.current?.();
        };
    }, [active, stageRef, commit, setGesture]);
    /** Совместимое mouse-событие сразу после касания (телефон эмулирует мышь) — не наше. */
    const fromTouch = useCallback(() => Date.now() - lastTouchEndRef.current < TOUCH_MOUSE_GUARD_MS, []);
    const onMouseDown = useCallback((e) => {
        if (e.button !== 0 || fromTouch() || isOnButton(e.target))
            return;
        if (!live.current.zoomable || !isZoomed(tRef.current))
            return;
        const stage = stageRef.current;
        if (stage === null)
            return;
        const frame = live.current.measure(stage);
        if (frame === null)
            return;
        e.preventDefault(); // без выделения и «перетаскивания картинки» браузером
        dragCleanupRef.current?.();
        dragRef.current = { start: { x: e.clientX, y: e.clientY }, startT: tRef.current, frame, moved: false };
        setGesture(true);
        const onMove = (ev) => {
            const d = dragRef.current;
            if (d === null)
                return;
            const dx = ev.clientX - d.start.x;
            const dy = ev.clientY - d.start.y;
            if (Math.abs(dx) > MOVE_SLOP || Math.abs(dy) > MOVE_SLOP)
                d.moved = true;
            commit(panBy(d.startT, dx, dy, d.frame), false);
        };
        const cleanup = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            dragCleanupRef.current = null;
            dragRef.current = null;
            setGesture(false);
        };
        const onUp = () => {
            if (dragRef.current?.moved === true)
                suppressClickUntilRef.current = Date.now() + CLICK_SUPPRESS_MS;
            cleanup();
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        dragCleanupRef.current = cleanup;
    }, [stageRef, commit, setGesture, fromTouch]);
    const onDoubleClick = useCallback((e) => {
        // двойной тап на телефоне уже обработан касаниями (браузер может добавить свой dblclick)
        if (fromTouch() || isOnButton(e.target) || !live.current.zoomable)
            return;
        const stage = stageRef.current;
        if (stage === null)
            return;
        const frame = live.current.measure(stage);
        if (frame === null)
            return;
        commit(toggleZoomAt(tRef.current, toBoxPoint({ x: e.clientX, y: e.clientY }, frame), frame), true);
        suppressClickUntilRef.current = Date.now() + CLICK_SUPPRESS_MS;
    }, [stageRef, commit, fromTouch]);
    const allowBackdropClose = useCallback(() => !isZoomed(tRef.current) && Date.now() >= suppressClickUntilRef.current, []);
    return {
        transform,
        zoomed: isZoomed(transform),
        gesturing,
        animating,
        allowBackdropClose,
        onMouseDown,
        onDoubleClick,
    };
}
//# sourceMappingURL=useZoomPan.js.map